<script setup lang="ts">
import DRBtnIcon from "@/components/UI/DRBtnIcon.vue";
import DRConfirmPopup from "@/components/UI/feedbacks/DRConfirmPopup.vue";
import { computed, ref } from "vue";
import type { NoteConfig, Category, Layer } from "@/types/legend.types";
import { HatchName } from "@/hatches/hatch-list";
import HatchLibrary from "@/hatches/library";
import LegendColorPicker from "./LegendColorPicker.vue";
import DebounceInput from "@/components/UI/form/DebounceInput.vue";
import LayerOption from "./LayerOption.vue";
import HatchSelector from "./HatchSelector.vue";
import { defaultColor } from "./edit-legend.models";
import DRBtn from "@/components/UI/DRBtn.vue";
import { getNoteLineweightOptions, linetypeLabel } from "./utils";
import { ENTITY_LINETYPES } from "@/drawing/linetype/utils";
import { v4 } from "uuid";
import { useTranslation } from "i18next-vue";

const props = withDefaults(
  defineProps<{
    modelValue: NoteConfig;
    layers: Layer[];
    type?: "default" | "photo";
    categories: Category[];
  }>(),
  {
    type: "default",
  }
);

const emits = defineEmits<{
  (e: "update:modelValue", value: NoteConfig): void;
  (e: "update:categoryIds", value: string[]): void;
  (e: "remove"): void;
  (e: "new-layer", value: { id: string; name: string }): void;
  (e: "duplicate"): void;
}>();

const note = computed({
  get: () => props.modelValue,
  set: (value: NoteConfig) => emits("update:modelValue", value),
});

const noteProps = computed({
  get: () => note.value.props,
  set: (value: NoteConfig["props"]) => {
    note.value = {
      ...note.value,
      props: value,
    };
  },
});

const name = computed({
  get: () => note.value.name,
  set: (value: string) => {
    note.value = {
      ...note.value,
      name: value.trim(),
    };
  },
});

const label = computed({
  get: () => note.value.label || "",
  set: (value: string) => {
    note.value = {
      ...note.value,
      label: value.trim(),
    };
  },
});

const layerId = computed({
  get: () => note.value.props.layerId,
  set: (value: string | undefined) => {
    note.value = {
      ...note.value,
      props: {
        ...note.value.props,
        layerId: value,
      },
    };
  },
});
const hatchname = computed({
  get: () => note.value.hatch?.name,
  set: (value?: HatchName) => {
    note.value = {
      ...note.value,
      hatch: value
        ? {
            ...note.value.hatch,
            name: value,
          }
        : undefined,
    };
  },
});
// Tant que l'utilisateur n'a pas saisi de userScale, le moteur retombe sur la baseScale
// de la bibliothèque (NoteMenuState.computeNoteConfig) — on l'affiche ici en placeholder
// pour indiquer la valeur effective, sans l'écrire dans les données tant que non modifiée.
const hatchBaseScale = computed(
  () => HatchLibrary.getByName(note.value.hatch?.name ?? "")?.baseScale
);
const rotation = computed({
  get: () => note.value.hatch?.rotation,
  set: (value?: number | undefined) => {
    if (!note.value.hatch?.name) return;
    note.value = {
      ...note.value,
      hatch: {
        ...note.value.hatch,
        rotation: value,
      },
    };
  },
});
const hatchUserScale = computed({
  get: () => note.value.hatch?.userScale,
  set: (value?: number) => {
    if (!note.value.hatch?.name) return;
    note.value = {
      ...note.value,
      hatch: { ...note.value.hatch, userScale: value },
    };
  },
});

const lineweight = computed({
  get: () => noteProps.value.lineweight,
  set: (value?: number) => {
    noteProps.value = { ...noteProps.value, lineweight: value };
  },
});
const linetype = computed({
  get: () => noteProps.value.linetype,
  set: (value?: string) => {
    noteProps.value = { ...noteProps.value, linetype: value };
  },
});
const { t } = useTranslation();
// Fonction du t() actif plutôt qu'une const figée au chargement du module, pour rester
// à jour si la langue change (voir getNoteLineweightOptions).
const lineweightOptions = computed(() => getNoteLineweightOptions(t));
const linetypeOptions = ENTITY_LINETYPES;

const isAddingLayer = ref(false);
const onAddLayer = () => {
  isAddingLayer.value = true;
};

const newLayerName = ref("");

const clear = () => {
  newLayerName.value = "";
  isAddingLayer.value = false;
};
const onConfirm = () => {
  const layerName = newLayerName.value?.trim();
  if (!layerName) {
    clear();
    return;
  }

  // L'id est généré ici (pas dans le parent) pour pouvoir sélectionner immédiatement le
  // calque nouvellement créé, sans attendre un aller-retour pour connaître son id.
  const newLayerId = v4();
  emits("new-layer", { id: newLayerId, name: layerName });
  layerId.value = newLayerId;
  clear();
};

const isLayerColor = computed(() => {
  return note.value.props.color === "kByLayer";
});

const concernedLayer = computed(() => {
  return props.layers.find((layer) => layer.id === layerId.value);
});

const changeIsLayerColor = () => {
  noteProps.value = {
    ...noteProps.value,
    color: isLayerColor.value ? "0,0,0" : "kByLayer",
  };
};

const noteColor = computed(() => {
  if (isLayerColor.value) {
    return concernedLayer.value?.color || "0,0,0";
  }
  return noteProps.value.color;
});

const updateColor = (value: string) => {
  noteProps.value = {
    ...noteProps.value,
    color: value,
  };
};

// L'appartenance aux catalogues est possédée par Category.noteIds, pas par la note :
// on la lit en cherchant quels catalogues référencent cet id, et on émet le nouvel
// ensemble au parent (NotesConfigSection) plutôt que de la stocker ici.
const noteCategories = computed({
  get: () =>
    props.categories.filter((c) => c.noteIds.includes(note.value.id)).map((c) => c.id),
  set: (value: string[]) => {
    emits("update:categoryIds", value);
  },
});

const onRemoveLayer = () => {
  noteProps.value = {
    ...noteProps.value,
    layerId: undefined,
    color: isLayerColor.value ? defaultColor : noteProps.value.color,
  };
};
</script>

<template>
  <div class="rounded p-2 d-flex gap-3 align-items-center hoverable-white">
    <HatchSelector
      v-if="props.type === 'default'"
      v-model="hatchname"
      v-model:rotation="rotation"
      v-model:user-scale="hatchUserScale"
      :base-scale="hatchBaseScale"
    />

    <!--
      Un seul conteneur flex-wrap plutôt que deux moitiés figées (col-xl-6/col-xl-6) :
      chaque champ porte son propre flex-basis/min-width, donc au rétrécissement les
      champs passent à la ligne suivante au lieu de tous se comprimer en même temps
      (c'est ce qui rendait le calque sélectionné illisible).
    -->
    <div class="col field-row">
      <DebounceInput
        class="field-name"
        v-model="name"
        :placeholder="$t('name')"
      ></DebounceInput>
      <DebounceInput
        v-if="props.type === 'default'"
        class="field-label"
        v-model="label"
        :placeholder="$t('label')"
      ></DebounceInput>

      <el-select
        v-if="props.type === 'default'"
        v-model="noteCategories"
        multiple
        collapse-tags
        collapse-tags-tooltip
        :placeholder="$t('dashboard:catalogues')"
        class="field-categories"
      >
        <el-option
          v-for="item in categories"
          :key="item.id"
          :label="item.name"
          :value="item.id"
        />
      </el-select>

      <el-select
        v-model="layerId"
        :placeholder="$t('dashboard:no.layer')"
        class="field-layer"
      >
        <el-option
          v-for="item in layers"
          :key="item.id"
          :label="item.name"
          :value="item.id"
        >
          <LayerOption :layer="item" />
        </el-option>
        <template #footer>
          <div v-if="!isAddingLayer" class="d-flex flex-column gap-1">
            <DRBtn
              class="w-full"
              @click="() => onRemoveLayer()"
              variant="outline-danger"
              :label="$t('withdraw')"
            >
            </DRBtn>

            <DRBtn
              variant="outline"
              class="w-full"
              @click="onAddLayer"
              :label="$t('new')"
            >
            </DRBtn>
          </div>
          <template v-else>
            <el-input
              v-model="newLayerName"
              class="option-input"
              :placeholder="$t('dashboard:layername')"
              size="small"
              @keyup.enter="onConfirm"
            />
            <div class="mt-1 d-flex gap-1 justify-content-between">
              <DRBtn
                variant="outline"
                size="xs"
                :label="$t('cancel')"
                @click="clear"
              />
              <DRBtn
                variant="primary"
                size="xs"
                :label="$t('confirm')"
                @click="onConfirm"
              />
            </div>
          </template>
        </template>
      </el-select>

      <el-select
        v-model="lineweight"
        :placeholder="$t('dashboard:lineweight')"
        clearable
        class="field-lineweight"
      >
        <el-option
          v-for="option in lineweightOptions"
          :key="option.value"
          :label="option.label"
          :value="option.value"
        />
      </el-select>

      <el-select
        v-model="linetype"
        :placeholder="$t('dashboard:linetype')"
        clearable
        class="field-linetype"
      >
        <el-option
          v-for="option in linetypeOptions"
          :key="option"
          :label="linetypeLabel(option, t)"
          :value="option"
        />
      </el-select>

      <el-checkbox
        class="field-layer-color"
        :model-value="isLayerColor"
        @update:modelValue="changeIsLayerColor"
        :disabled="!layerId"
        text-color="primary"
        ><span class="font-mini">{{
          $t("dashboard:color.of.layer")
        }}</span></el-checkbox
      >

      <LegendColorPicker
        :disabled="isLayerColor"
        class="col-auto"
        :model-value="noteColor"
        @update:model-value="updateColor"
      />
    </div>
    <div
      class="ps-4 col-auto d-flex gap-1 align-items-center justify-content-end"
    >
      <template v-if="type === 'default'">
        <DRBtnIcon
          icon="bi-copy"
          variant="flat"
          :size="30"
          @click="$emit('duplicate')" />

        <DRConfirmPopup :title="$t('confirmDelete')" @confirm="$emit('remove')">
          <DRBtnIcon
            icon="bi-trash3"
            variant="flat-danger"
            :size="30"
          /> </DRConfirmPopup
      ></template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.font-mini {
  font-size: 0.8rem;
  font-weight: 400;
}

.field-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;
}

// name/label restent au-dessus, avec un ratio raisonnable entre les deux plutôt qu'un
// col-4 sur label qui l'étalait bien au-delà de ce que son contenu nécessite.
.field-name {
  flex: 2 1 160px;
  min-width: 140px;
}

.field-label {
  flex: 1 1 90px;
  min-width: 80px;
  max-width: 140px;
}

.field-categories {
  flex: 1 1 150px;
  min-width: 130px;
  max-width: 220px;
}

// Le calque est l'information la plus consultée de la ligne : min-width garanti pour
// qu'il reste lisible, quitte à passer à la ligne suivante plutôt que se comprimer.
.field-layer {
  flex: 2 1 180px;
  min-width: 160px;
}

.field-lineweight {
  flex: 0 0 110px;
  width: 110px;
}

.field-linetype {
  flex: 0 0 130px;
  width: 130px;
}

.field-layer-color {
  flex: 0 0 auto;
}

.el-checkbox__input.is-checked .el-checkbox__inner {
  background-color: var(--bs-dark);
  border-color: var(--bs-dark);
}

.el-checkbox__label {
  color: var(--bs-dark);
}

.el-checkbox__input.is-checked + .el-checkbox__label {
  color: var(--bs-dark) !important;
}
</style>
